import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Common, color } from '@stockbit/ui';
import debounce from 'lodash.debounce';

import AuthCardLayout from 'global/AuthCardLayout';
import Image from 'global/Image';
import useForm from 'hooks/useForm';
import { clevertapEvent } from 'utils/clevertap';

import InputForm from '../InputForm';
import { usePasswordVisibility } from '../hooks';
import emailRegisterSlice, { effects, selectors } from './slice';
import rootSlice, { selectors as rootSelector } from '../slice';
import {
  validateEmail,
  validatePasswordConf,
  handleRegistrationError,
} from '../utils';
import {
  formTypes,
  hints,
  registerTypes,
  emailStatuses,
  errorMessage,
} from '../constants';

const { Button, Text } = Common;

interface EmailRegisterProps {
  onRegister({ fullname, email, username, password, passwordConf }): void;
}

function EmailRegister({ onRegister }: EmailRegisterProps): JSX.Element {
  const dispatch = useDispatch();
  const { validations } = useSelector(selectors);

  const {
    email: initialEmail,
    emailStatus,
    error,
    fullname: initialFullname,
    tempKey,
    type,
    username: initialUsername,
  } = useSelector(rootSelector);

  const isSocialMediaForm = type !== registerTypes.EMAIL;
  const isFacebookWithoutEmail =
    type === registerTypes.FACEBOOK && !initialEmail;
  const isEmailNotVerified = emailStatus === emailStatuses.NOT_VERIFIED;
  const canChangeEmail =
    !isSocialMediaForm || isFacebookWithoutEmail || isEmailNotVerified;

  const [fullname, setFullname] = useForm({ initialState: initialFullname });
  const [email, setEmail] = useForm({ initialState: initialEmail });
  const [username, setUsername] = useForm({ initialState: initialUsername });
  const [password, setPassword] = useForm({ initialState: '' });
  const [passwordConf, setPasswordConf] = useForm({ initialState: '' });
  const [passwordConfError, setPasswordConfError] =
    useState<string | null>(null);
  const [isPasswordConfChecked, setPasswordConfChecked] =
    useState<boolean>(false);

  const { getInputType, toggleVisibility } = usePasswordVisibility();

  const validateValues = (): boolean => {
    const fields = [fullname, email, username];
    const isPasswordFilled = isSocialMediaForm || (password && passwordConf);
    const isInputFilled = fields.every((item) => item.trim().length > 0);
    const isInputNoError = Object.values(validations).every(
      (item) => !item.error,
    );
    const isInputValid = isInputNoError && !passwordConfError;

    return isInputFilled && isInputValid && isPasswordFilled;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isInputValid = validateValues();
    if (!isInputValid) return;

    clevertapEvent('Login Selected', {
      'Login Method': registerTypes.EMAIL,
    });

    if (error === errorMessage.SOCIAL_EMAIL_REGISTERED) {
      handleRegistrationError(errorMessage.SOCIAL_EMAIL_REGISTERED, email);
      return;
    }

    if (!canChangeEmail) {
      dispatch(rootSlice.actions.changeForm({ target: formTypes.PHONE }));
      return;
    }

    onRegister({
      fullname,
      email,
      username,
      password,
      passwordConf,
    });
  };

  const debouncedPassword = debounce(() => {
    const errorConf = validatePasswordConf(password, passwordConf);
    setPasswordConfError(errorConf);
    setPasswordConfChecked(true);
  }, 500);

  useEffect(() => {
    if (passwordConf) {
      debouncedPassword(); // Debounced password validation
    }

    return () => {
      debouncedPassword.cancel();
    };
  }, [password, passwordConf]);

  useEffect(() => {
    if (!tempKey) return;

    const fieldsToValidate = [
      { field: 'fullname', value: fullname },
      { field: 'username', value: username },
    ];

    fieldsToValidate.forEach(({ field, value }) => {
      if (value && field) {
        dispatch(effects.validateField({ field, value, tempKey }));
      }
    });
  }, []);

  const handleChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canChangeEmail) return;
    setEmail(e);

    // Validate email
    const { value } = e.target;
    const validation = validateEmail(value);
    dispatch(emailRegisterSlice.actions.storeFieldValidation(validation));
  };

  const debounceValidations = debounce(({ name, value }) => {
    if (!tempKey) return;
    dispatch(effects.validateField({ field: name, value, tempKey }));
  }, 500);

  const handleFormBlur = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      dispatch(
        emailRegisterSlice.actions.resetFieldValidation({ field: name }),
      );
      if (isSocialMediaForm) {
        debounceValidations({ name, value });
      }
    },
    [],
  );

  const renderToggleButton = (inputName: string): JSX.Element => {
    const isShown = getInputType(inputName) === 'text';
    const iconSrc = isShown
      ? '/icon/register/icon-hide.svg'
      : '/icon/register/icon-show.svg';

    return (
      <button
        tabIndex={-1} // Prevent focus on button
        onClick={() => toggleVisibility(inputName)}
        type="button"
        className="btn-toggle"
      >
        <Image src={iconSrc} />
      </button>
    );
  };

  const isValidForm = validateValues();

  return (
    <AuthCardLayout withLoginLink withTermsLink wrapperClassName="register">
      <form onSubmit={handleSubmit}>
        <InputForm
          errorMsg={validations.fullname.error}
          isChecked={validations.fullname.isChecked}
          name="fullname"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFullname(e);
            handleFormBlur(e);
          }}
          placeholder="Your Name"
          type="textbox"
          value={fullname}
          hint={hints.NAME}
          required
          id="fullname"
        />
        <InputForm
          errorMsg={validations.email.error}
          isChecked={validations.email.isChecked}
          name="email"
          onChange={handleChangeEmail}
          placeholder="Your Email"
          type="textbox"
          value={email}
          disabled={!canChangeEmail}
          hint={hints.EMAIL}
          required
          id="email"
        />
        <InputForm
          errorMsg={validations.username.error}
          name="username"
          isChecked={validations.username.isChecked}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setUsername(e);
            handleFormBlur(e);
          }}
          placeholder="Username"
          type="textbox"
          value={username}
          hint={hints.USERNAME}
          required
          id="username"
        />
        {!isSocialMediaForm && (
          <>
            <InputForm
              errorMsg={validations.password.error}
              isChecked={validations.password.isChecked}
              name="password"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setPassword(e);
                handleFormBlur(e);
              }}
              placeholder="Password"
              type={getInputType('password')}
              value={password}
              hint={hints.PASSWORD}
              required
              id="password"
              toggleButton={renderToggleButton('password')}
              autoComplete="new-password"
            />
            <InputForm
              onChange={setPasswordConf}
              placeholder="Confirm Password"
              type={getInputType('confirmation')}
              value={passwordConf}
              errorMsg={passwordConfError}
              isChecked={isPasswordConfChecked}
              required
              id="confirm-password"
              toggleButton={renderToggleButton('confirmation')}
              autoComplete="new-password"
            />
          </>
        )}

        <Button
          onClick={handleSubmit}
          htmlType="submit"
          type="primary"
          mt="48px"
          height="44px"
          block
          disabled={!isValidForm}
          id="register-button"
        >
          <Text family="bold" color={color.light}>
            Register
          </Text>
        </Button>
      </form>
    </AuthCardLayout>
  );
}

export default EmailRegister;
